import { LoadingState } from '@lcs/shared/api';
import { createFeatureSelector, on } from '@ngrx/store';
import { OrderingDirections } from 'generated/graphql-types';
import { RegisterState } from 'src/app/common-components/register/models/register-state';
import {
   RegisterAdapter,
   createRegisterAdapter,
} from 'src/app/common-components/register/models/register-state.adapter';
import * as ExtensionDetailActions from '../../extension-details/state/extension-detail.actions';
import { AccountOrderingOptions, AccountRegisterOptions } from '../models/account-register-options';
import { AccountRegisterListItem } from '../models/account-register-results.model';
import { AccountsFilterOption } from '../models/accounts-filter-option';
import * as accountRegisterActions from './account-register.actions';

export const accountRegisterFeatureKey = 'accountRegister';
export interface AccountRegisterState
   extends RegisterState<AccountRegisterListItem, AccountRegisterOptions, AccountOrderingOptions> {
   filters: AccountsFilterOption[];
   includeInactive: boolean;
}

const adapter: RegisterAdapter<AccountRegisterListItem, AccountRegisterOptions, AccountOrderingOptions> =
   createRegisterAdapter<AccountRegisterListItem, AccountRegisterOptions, AccountOrderingOptions>({
      selectId: (accountRegisterItem) => {
         return accountRegisterItem.accountId ?? 0;
      },
   });

const initialState: AccountRegisterState = {
   ...adapter.getInitialState(),
   filters: [],
   includeInactive: false,
   orderingOptions: [{ direction: OrderingDirections.Asc, field: 'name' }],
};

export const accountRegisterReducer = adapter.createReducer(
   accountRegisterActions.accountRegisterAdapterActions,
   initialState,
   on(accountRegisterActions.accountRegisterAdapterActions.pageEnter, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(accountRegisterActions.accountRegisterAdapterActions.loadSuccess, (state, action) => {
      return {
         ...state,
         accounts: action.items,
         callState: LoadingState.Loaded,
         endCursor: action.endCursor,
         hasNextPage: action.hasNextPage,
         totalCount: action.totalCount,
      };
   }),
   on(accountRegisterActions.accountRegisterAdapterActions.quickFilterChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         quickFilters: {
            ...state.quickFilters,
            ...action.quickFilters,
         },
      };
   }),
   on(accountRegisterActions.accountRegisterInitialFilter, (state, action) => {
      return {
         ...state,
         filters: action.filters,
      };
   }),
   on(accountRegisterActions.accountRegisterAdapterActions.orderingOptionsChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         orderingOptions: state.orderingOptions
            ? [
                 ...state.orderingOptions
                    .filter((option) => !action.orderingOptions.some((newOption) => newOption.field === option.field))
                    .filter((option) => option.direction !== OrderingDirections.NotSet),
                 ...action.orderingOptions,
              ]
            : action.orderingOptions,
      };
   }),
   on(accountRegisterActions.setOrderingOptionsFromSessionStorage, (state, action) => {
      return {
         ...state,
         orderingOptions: action.orderingOptions,
      };
   }),
   on(accountRegisterActions.accountRegisterAdapterActions.loadIdPageSuccess, (state, action) => {
      return {
         ...state,
         ids: [...(state.ids as number[]), ...(action.ids as number[])],
      };
   }),
   on(ExtensionDetailActions.resetMwiConfirmation, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(
      ExtensionDetailActions.resetMwiSuccess,
      ExtensionDetailActions.resetMwiFailure,
      ExtensionDetailActions.resetMwiCancel,
      (state) => {
         return {
            ...state,
            callState: LoadingState.Loaded,
         };
      }
   )
);
export const accountRegisterSelector = createFeatureSelector<AccountRegisterState>(accountRegisterFeatureKey);
export const selectors = adapter.getSelectors(accountRegisterSelector);
export const {
   selectAll,
   selectCallState,
   selectTotalCount,
   selectQuickFilters,
   selectEndCursor,
   selectOrderingOptions,
   selectIds,
} = adapter.getSelectors(accountRegisterSelector);
